import React, { useState } from 'react';
import { Box, Typography, Divider, Link, TextField, Button, IconButton, Tooltip } from '@mui/material';
import {
  Image as ImageIcon,
  RocketLaunch as RocketIcon,
  Build as BuildIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Payment as CreditIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { CREDITS_GUIDE_ANCHOR, WEBSITES_PATH } from '../utils/routes';

const ONBOARDING_PATH = '/wp-admin/admin.php?page=improveseo_onboarding';

// ── colour tokens (mirrors Dashboard) ────────────────────────────────────────
const C = {
  text: '#0F172A',
  textMuted: '#64748B',
  warmCard: '#F4F1DE',
  accentTeal: '#1C7C73',
  accentOrange: '#FF7A59',
  gradientOrange: 'linear-gradient(45deg, #A7391E 0%, #FF7A59 100%)',
  placeholderBorder: '#CBD5E1',
  placeholderBg: 'rgba(0,0,0,0.02)',
  placeholderFg: '#94A3B8',
};

// ── Image placeholder (visual guides added later) ─────────────────────────────
const ImagePlaceholder = ({ label = 'Image placeholder' }) => (
  <Box
    sx={{
      mt: 2,
      border: `2px dashed ${C.placeholderBorder}`,
      borderRadius: '12px',
      bgcolor: C.placeholderBg,
      minHeight: 180,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      color: C.placeholderFg,
      px: 2,
      textAlign: 'center',
    }}
  >
    <ImageIcon sx={{ fontSize: 40 }} />
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

// ── Numbered step ─────────────────────────────────────────────────────────────
const Step = ({ number, title, children, image }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '9999px',
        background: C.gradientOrange,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 800,
        fontSize: 18,
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {number}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 16,
          lineHeight: '24px',
          color: C.text,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        component="div"
        sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, lineHeight: '22px', color: '#000814' }}
      >
        {children}
      </Typography>
      <ImagePlaceholder label={image} />
    </Box>
  </Box>
);

const CardHeader = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
    <Icon sx={{ color: C.accentOrange, fontSize: 24 }} />
    <Box>
      <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, lineHeight: '28px', color: '#191C1D' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: C.textMuted }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const cardSx = {
  bgcolor: C.warmCard,
  borderRadius: '24px',
  p: { xs: '24px', md: '32px' },
  boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

// ── Onboarding link generator ─────────────────────────────────────────────────
const OnboardingLinkGenerator = () => {
  // 2.1 — the Websites tab's "Connect Website" action deep-links here as
  // ?domain=example.com, so the user arrives with their site already filled in
  // instead of having to retype it. Read once on mount; typing then owns the
  // field. Reading window.location keeps this component usable from the public
  // /tutorials route too, which renders outside the dashboard's router context
  // in exactly the same way.
  const [domain, setDomain] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('domain') || '';
    } catch (_) {
      return '';
    }
  });
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    // Accept domains with or without a protocol / trailing slash.
    const clean = domain
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '');
    setLink(clean ? `https://${clean}${ONBOARDING_PATH}` : '');
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* 1.3 — placeholder was "yourdomain.com". Verified repo-wide: it was
            the only occurrence, so nothing else needed the "same concept"
            carve-out. */}
        <TextField
          size="small"
          placeholder="yourwebsite.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleGenerate();
            }
          }}
          sx={{ flex: 1, minWidth: 220, bgcolor: '#fff', borderRadius: '8px' }}
        />
        <Button
          variant="contained"
          onClick={handleGenerate}
          sx={{
            background: C.gradientOrange,
            color: '#fff',
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            borderRadius: '8px',
            px: 3,
            '&:hover': { opacity: 0.9, background: C.gradientOrange },
          }}
        >
          Generate
        </Button>
      </Box>

      {link && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: '#fff',
            border: `1px solid ${C.placeholderBorder}`,
            borderRadius: '8px',
            px: 2,
            py: 1,
          }}
        >
          <Link
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ color: C.accentTeal, fontWeight: 700, fontSize: 14, wordBreak: 'break-all' }}
          >
            {link}
          </Link>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? (
                  <CheckIcon sx={{ fontSize: 18, color: C.accentTeal }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 18, color: C.textMuted }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Open link">
              <IconButton size="small" component="a" href={link} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon sx={{ fontSize: 18, color: C.textMuted }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Quick Start Guide ─────────────────────────────────────────────────────────
// Renamed from "Tutorial" (page title, nav item and route — see
// QUICK_START_PATH in Dashboard.js).
//
// Rendered in two places from this one component so the copy can never drift:
//   • inside the CMS as the "Quick Start Guide" tab (standalone = false), and
//   • at the public, no-login /tutorials route (standalone = true), which adds
//     its own page chrome since there's no dashboard shell around it.
const Tutorial = ({ standalone = false }) => {
  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', maxWidth: 820, width: '100%' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 800,
            fontSize: { xs: 28, md: 36 },
            lineHeight: '40px',
            letterSpacing: '-0.025em',
            color: '#191C1D',
          }}
        >
          {/* OLD: Tutorial */}
          Quick Start Guide
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 18, lineHeight: '28px', color: '#000814' }}>
          Get ImproveSEO connected to your WordPress site and publish your first post.
        </Typography>
      </Box>

      {/* Automatic setup */}
      <Box sx={cardSx}>
        <CardHeader icon={RocketIcon} title="Automatic setup" subtitle="The fastest way to get started" />

        <Step
          number={1}
          /* OLD: "Install & activate the plugin" */
          title="Install & Activate the ImproveSEO Plugin"
          image="Visual guide: installing & activating the plugin"
        >
          Install and activate the ImproveSEO plugin on the WordPress site you
          want to optimize.
        </Step>

        <Step
          number={2}
          title="Run the guided onboarding"
          image="Visual guide: the Onboarding link & guided setup"
        >
          {/* 1.2 — OLD: "Enter your site's domain to generate your Onboarding
              link, then open it to automatically set up the plugin and generate
              your first post, guided every step of the way."
              "Wordpress" is spelled as the client wrote it. */}
          Enter your Wordpress website address to generate your{' '}
          <strong>Onboarding</strong> link, then open it to automatically set up
          the plugin and generate your first post, guided every step of the way.
          <OnboardingLinkGenerator />
        </Step>
      </Box>

      {/* OR separator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Divider sx={{ flex: 1, borderColor: C.placeholderBorder }} />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 13, color: C.textMuted, letterSpacing: '0.08em' }}>
          OR SET UP MANUALLY
        </Typography>
        <Divider sx={{ flex: 1, borderColor: C.placeholderBorder }} />
      </Box>

      {/* Manual setup */}
      <Box sx={cardSx}>
        <CardHeader icon={BuildIcon} title="Manual setup" subtitle="Connect the plugin yourself" />

        {/* ── OLD manual Step 1 ────────────────────────────────────────────
        <Step number={1} title="Add your API key" image="Visual guide: copying the API key & pasting it into the plugin">
          Go to <Link component={RouterLink} to="/settings" …>Settings</Link> and copy your
          API key, then paste it into the <strong>API Key</strong> field in the plugin's settings.
        </Step>
            ── END OLD ─────────────────────────────────────────────────────── */}
        <Step
          number={1}
          title="Install & Activate the ImproveSEO Plugin"
          image="Visual guide: installing the plugin & finding the Settings section"
        >
          Install and activate the ImproveSEO plugin on the WordPress site you want
          to optimize. Navigate to the Settings section on the left nav menu. You
          will paste API Key and Site Code here as explained in Step 3.
        </Step>

        {/* ── OLD manual Step 2 body ───────────────────────────────────────
            "To add a website to your panel, open Websites (opens in a new tab)
             and add your website, then copy the generated site code."
            ── END OLD ─────────────────────────────────────────────────────── */}
        <Step
          number={2}
          title="Add your website"
          image="Visual guide: adding a website & copying the site code"
        >
          To add a new Wordpress website to your account, open{' '}
          <Link
            href={WEBSITES_PATH}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ color: C.accentTeal, fontWeight: 700 }}
          >
            Websites
          </Link>{' '}
          (opens in a new tab) and add your website. Note: to add more than one
          website to your account, you will need to subscribe to the Grow plan or
          higher.
        </Step>

        {/* ── OLD manual Step 3 ────────────────────────────────────────────
        <Step number={3} title="Connect & test" image="…">
          Paste the site code into the <strong>Site Key / Code</strong> section in the plugin,
          then run <strong>Test Server Connection</strong>. If it succeeds, you're good to go!
        </Step>
            ── END OLD ─────────────────────────────────────────────────────── */}
        <Step
          number={3}
          /* capital T in "Test" is deliberate — client copy */
          title="Connect & Test"
          image="Visual guide: pasting the API Key & Site Code, then testing the server connection"
        >
          Find the API Key and Site Code on the{' '}
          <Link
            component={RouterLink}
            to={WEBSITES_PATH}
            underline="hover"
            sx={{ color: C.accentTeal, fontWeight: 700 }}
          >
            Websites
          </Link>{' '}
          tab, and copy and paste them one by one into the designated fields in the
          Settings section that you want to connect. Click the 'Save' button. Then
          run Test Server Connection. If it succeeds, you're good to go!
        </Step>
      </Box>

      {/* ── How credits are used ────────────────────────────────────────────
          Explains the consumption ORDER in general terms. It once received the
          "Which credits are used first →" link from the Credits page tooltip,
          but that link was specified to open the breakdown panel beside it —
          which names the user's own batches and their expiry dates — so it no
          longer routes here. The section stays: it is the conceptual half of
          the same answer, and the guide is where a reader looks for it. The id
          remains a stable anchor for linking to this section directly. */}
      <Box id={CREDITS_GUIDE_ANCHOR} sx={{ ...cardSx, scrollMarginTop: 88 }}>
        <CardHeader
          icon={CreditIcon}
          title="How credits are used"
          subtitle="One balance, spent at different rates"
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px', color: '#191C1D' }}>
            You have a single pool of credits. Content and images draw from it, at different
            rates — so the same balance stretches further on short posts than on long articles.
            Keyword lists are free.
          </Typography>

          <Box component="ul" sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px' }}>
              <strong>SEO content</strong> — 6 to 14 credits, depending on post size.
            </Typography>
            <Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px' }}>
              <strong>AI image</strong> — 5 credits.
            </Typography>
            <Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px' }}>
              <strong>Keyword list</strong> — free (your plan sets a daily limit).
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px', color: '#191C1D' }}>
            Re-generating something you have already made costs less than the first generation,
            because the groundwork is already done.
          </Typography>

          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 16, mt: 1 }}>
            Which credits are used first
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px', color: '#191C1D' }}>
            Credits are always spent in the order they expire — the ones with the nearest expiry
            date go first, so nothing lapses while newer credits sit unused. In practice that
            usually means your plan credits are spent before any you have bought, since plan
            credits have the shorter life.
          </Typography>

          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 16, mt: 1 }}>
            How long credits last
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px' }}>
              <strong>Plan credits</strong> — included with your subscription, usable for two
              billing cycles. Unused ones from last cycle are still yours until that date, and
              anything granted mid-cycle (an upgrade, say) expires with the cycle it belongs to.
            </Typography>
            <Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: '24px' }}>
              <strong>Purchased credits</strong> — credits you have bought, valid for 12 months
              from the date of purchase. Buying more renews that 12 months for your whole
              purchased balance, so they all expire on the same date.
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, lineHeight: '22px', color: C.textMuted }}>
            We email you before anything is due to expire, so you have time to use it.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  if (!standalone) return body;

  // Public /tutorials page — no dashboard shell, so supply page chrome. Kept
  // deliberately minimal: same content, its own header, and a way back in.
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFA', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 64,
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, background: '#fff' }}>
            <img src="/improvelogo.png" alt="ImproveSEO" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </Box>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 16, color: '#0F172A' }}>
            ImproveSEO
          </Typography>
        </Box>
        <Link
          component={RouterLink}
          to="/overview"
          underline="none"
          sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: C.accentTeal, whiteSpace: 'nowrap' }}
        >
          Go to dashboard →
        </Link>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', p: { xs: '24px', md: '48px' } }}>
        {body}
      </Box>
    </Box>
  );
};

export default Tutorial;
